import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";
import { CheckmarkCircleRegular } from "@fluentui/react-icons";
import { FluentSpinner } from "@/components/fluent-ui";
import { useApi } from "@/hooks/useApi";
import { DynamicFieldsApi } from "@/services/api/dynamic-fields-api";
import { ProjectsApi } from "@/services/api/projects-api";
import { ProjectTemplatesApi } from "@/services/api/project-templates-api";
import { useAuth } from "@/services/auth/use-auth";
import type { TriyoDynamicField, TriyoProject, TriyoProjectTemplate } from "@/types/models";
import { DealDateInput } from "./DealDateInput";
import { DealDynamicFields } from "./DealDynamicFields";
import { DealTeam } from "./DealTeam";
import { initializeDynamicFields, validateDynamicFields } from "./deal-dynamic-fields";
import {
  buildTemplateTeamSelection,
  emptyDealTeam,
  setAllMembers,
  toggleMember,
  toggleOwner,
  type DealTeamSelection,
} from "./deal-team";
import { formatLongCalendarDate } from "@/utils/calendar-date";
import {
  CREATE_DEAL_ACCESS_DENIED,
  DEAL_PRIORITIES,
  type DealPriority,
  addDaysToDateInput,
  buildDealPayload,
  isForbiddenError,
  type DealPersonOption,
  normalizeRoster,
  parseDateInput,
  parseApiErrorMessage,
  toDateInputValue,
} from "./create-deal-helpers";
import "./CreateDeal.scss";

export interface CreateDealProps {
  /** Invoked on Cancel and on Done from the success screen (dialog/back host). */
  onClose?: () => void;
  /** Invoked after the API confirms creation (e.g. so the Deals list can refresh). */
  onCreated?: (project: TriyoProject) => void;
}

const STEPS = ["Deal Information", "Deal Team"] as const;

interface CreateDealSetup {
  templates: TriyoProjectTemplate[];
  defaultTemplate: TriyoProjectTemplate | null;
}

/** Loads the roster (fatal on failure) and templates (optional, best-effort). */
async function fetchSetup(): Promise<CreateDealSetup> {
  const [templates, defaultTemplate] = await Promise.allSettled([
    ProjectTemplatesApi.getTemplatesForProject(),
    ProjectTemplatesApi.getDefaultTemplate(),
  ]);
  return {
    templates: templates.status === "fulfilled" ? templates.value : [],
    defaultTemplate: defaultTemplate.status === "fulfilled" ? defaultTemplate.value : null,
  };
}

function StepBars({ step }: { step: number }): JSX.Element {
  return (
    <div className="create-deal__step-bars" role="list" aria-label={`Step ${step} of ${STEPS.length}`}>
      {STEPS.map((label, index) => (
        <div
          key={label}
          role="listitem"
          aria-label={label}
          aria-current={index + 1 === step ? "step" : undefined}
          className={`create-deal__step-bar${index + 1 <= step ? " create-deal__step-bar--filled" : ""}`}
        />
      ))}
    </div>
  );
}

export function CreateDeal({ onClose, onCreated }: CreateDealProps): JSX.Element {
  const { user } = useAuth();

  const rosterFetcher = useCallback(async () => {
    try {
      return await ProjectsApi.getUsersAndGroups();
    } catch (error: unknown) {
      if (isForbiddenError(error)) throw new Error(CREATE_DEAL_ACCESS_DENIED);
      throw error instanceof Error ? error : new Error("The Deal roster could not be loaded.");
    }
  }, []);
  const roster = useApi(rosterFetcher);

  const setupFetcher = useCallback(() => fetchSetup(), []);
  const setup = useApi(setupFetcher);
  const templates = useMemo(
    () => (setup.data?.templates ?? []).filter((template) => !!template.Id && !!template.ProjectType),
    [setup.data]
  );

  const people = useMemo(() => normalizeRoster(roster.data), [roster.data]);

  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [start, setStart] = useState(() => toDateInputValue(new Date()));
  const [end, setEnd] = useState("");
  const [priority, setPriority] = useState<DealPriority>("Medium");
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [defaultsApplied, setDefaultsApplied] = useState(false);

  const [team, setTeam] = useState<DealTeamSelection>(() => emptyDealTeam());
  const [appliedTeamDefaultsKey, setAppliedTeamDefaultsKey] = useState<string | null>(null);
  const [memberSearch, setMemberSearch] = useState("");
  const [teamNotice, setTeamNotice] = useState("");

  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [creating, setCreating] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [created, setCreated] = useState<TriyoProject | null>(null);

  const [dynamicFields, setDynamicFields] = useState<TriyoDynamicField[]>([]);
  const [dynamicLoading, setDynamicLoading] = useState(false);
  const [dynamicLoadError, setDynamicLoadError] = useState(false);
  const [dynamicErrors, setDynamicErrors] = useState<Record<number, string>>({});
  const dynamicRequestRef = useRef(0);

  const clearError = (key: string) => setErrors((prev) => ({ ...prev, [key]: undefined }));

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.Id === templateId) ?? null,
    [templates, templateId]
  );
  const isMultiOwner = selectedTemplate?.IsMultiOwner ?? true;
  const canTransferOwner = selectedTemplate?.CanTransferOwner !== false;
  const currentUserKey = user?.Id ? `u-${user.Id}` : null;
  const teamDefaultsKey = [
    templateId ?? "none",
    selectedTemplate?.IsMultiOwner ?? true,
    selectedTemplate?.CanTransferOwner ?? true,
    ...(selectedTemplate?.Owners ?? []),
    "members",
    ...(selectedTemplate?.Members ?? []),
    "roster",
    currentUserKey ?? "unknown",
    ...people.map((person) => person.key),
  ].join("|");

  // Preselect the tenant default template and derive End = Start + Days, as Triyo.Web does.
  useEffect(() => {
    if (defaultsApplied || setup.loading) return;
    setDefaultsApplied(true);
    const fallback = setup.data?.defaultTemplate;
    const match = fallback?.Id ? templates.find((template) => template.Id === fallback.Id) : undefined;
    if (match?.Id) {
      setTemplateId(match.Id);
      if (match.Days && match.Days > 0) {
        setEnd((current) => current || addDaysToDateInput(toDateInputValue(new Date()), match.Days ?? 0));
      }
    }
  }, [defaultsApplied, setup.loading, setup.data, templates]);

  // Load the selected template's dynamic fields (objectId 0 = create), mirroring
  // Triyo.Web's ProjectDynamicFields effect. A failure is non-fatal: the Deal can
  // still be created, just without template-specific fields.
  useEffect(() => {
    const requestId = ++dynamicRequestRef.current;
    // Drop the previous template's values immediately — posting field ids from
    // template A against template B would attach the wrong data to the Deal.
    setDynamicFields([]);
    setDynamicErrors({});
    setDynamicLoadError(false);
    if (!templateId) {
      setDynamicLoading(false);
      return;
    }
    setDynamicLoading(true);
    DynamicFieldsApi.getProjectDynamicFields(templateId)
      .then((fields) => {
        if (requestId !== dynamicRequestRef.current) return;
        setDynamicFields(initializeDynamicFields(fields));
      })
      .catch(() => {
        // Surfaced rather than swallowed: an empty list and a failed request
        // look identical otherwise, and the Deal would be created without the
        // template's fields with no indication anything was missing.
        if (requestId !== dynamicRequestRef.current) return;
        setDynamicFields([]);
        setDynamicLoadError(true);
      })
      .finally(() => {
        if (requestId === dynamicRequestRef.current) setDynamicLoading(false);
      });
  }, [templateId]);

  // Apply template membership/ownership defaults whenever the roster or
  // selected template changes. User edits do not change teamDefaultsKey, so
  // the defaults are not reapplied while the form is being edited.
  useEffect(() => {
    if (people.length === 0 || appliedTeamDefaultsKey === teamDefaultsKey) return;
    setTeam(buildTemplateTeamSelection(people, selectedTemplate, currentUserKey));
    setAppliedTeamDefaultsKey(teamDefaultsKey);
    setTeamNotice("");
    setErrors((current) => (current.team ? { ...current, team: undefined } : current));
  }, [appliedTeamDefaultsKey, currentUserKey, people, selectedTemplate, teamDefaultsKey]);

  // Selection changes that Triyo.Web silently refuses (removing the last owner)
  // get an explanation here instead, since a dead checkbox reads as a bug.
  const applyTeamChange = (next: DealTeamSelection | null, blockedMessage: string) => {
    if (!next) {
      setTeamNotice(blockedMessage);
      return;
    }
    setTeamNotice("");
    setTeam(next);
    clearError("team");
  };

  const onToggleMember = (key: string) => {
    if (!canTransferOwner && team.ownerKeys.has(key)) {
      setTeamNotice("This template fixes the owner selection during Deal creation.");
      return;
    }
    applyTeamChange(
      toggleMember(team, key, currentUserKey),
      "A Deal needs at least one owner. Add another owner before removing this one."
    );
  };

  const onToggleOwner = (key: string) => {
    if (!canTransferOwner) {
      setTeamNotice("This template fixes the owner selection during Deal creation.");
      return;
    }
    applyTeamChange(toggleOwner(people, team, key, isMultiOwner), "A Deal needs at least one owner.");
  };

  const onToggleAllMembers = (checked: boolean) =>
    applyTeamChange(setAllMembers(people, team, checked, currentUserKey), "");

  const onTemplateChange = (value: string) => {
    const id = value ? Number(value) : null;
    setTemplateId(id);
    const template = templates.find((entry) => entry.Id === id);
    if (template && parseDateInput(start)) {
      setEnd(addDaysToDateInput(start, template.Days ?? 0));
    }
  };

  const onDynamicFieldChange = (fieldId: number, data: string) => {
    setDynamicFields((current) => current.map((field) => (field.Id === fieldId ? { ...field, Data: data } : field)));
    setDynamicErrors((current) => {
      if (!current[fieldId]) return current;
      const next = { ...current };
      delete next[fieldId];
      return next;
    });
  };

  const onDynamicFieldFilesChange = (fieldId: number, files: TriyoDynamicField["Files"]) => {
    setDynamicFields((current) =>
      current.map((field) => (field.Id === fieldId ? { ...field, Files: files ?? [] } : field))
    );
    setDynamicErrors((current) => {
      if (!current[fieldId]) return current;
      const next = { ...current };
      delete next[fieldId];
      return next;
    });
  };

  const nextFromStep1 = () => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = "Required";
    if (!parseDateInput(start)) next.start = "Required";
    if (!parseDateInput(end)) next.end = "Required";
    const startDate = parseDateInput(start);
    const endDate = parseDateInput(end);
    if (startDate && endDate && endDate < startDate) next.end = "End date cannot be before Start date";
    const nextDynamic = validateDynamicFields(dynamicFields);
    setErrors(next);
    setDynamicErrors(nextDynamic);
    if (Object.keys(next).length === 0 && Object.keys(nextDynamic).length === 0) setStep(2);
  };

  const handleCreate = async () => {
    if (team.memberKeys.size === 0) {
      setErrors({ team: "Select at least one Deal member." });
      return;
    }
    if (team.ownerKeys.size === 0) {
      setErrors({ team: "Select at least one Deal owner." });
      return;
    }
    // Dynamic fields live on step 1; send the user back if any became invalid.
    const nextDynamic = validateDynamicFields(dynamicFields);
    if (Object.keys(nextDynamic).length > 0) {
      setDynamicErrors(nextDynamic);
      setErrors({});
      setStep(1);
      return;
    }
    setErrors({});
    setSubmitError("");
    setCreating(true);
    try {
      const payload = buildDealPayload({
        name,
        description,
        start,
        end,
        priority,
        template: selectedTemplate,
        // Owners keep selection order so the creator stays the primary owner
        // (the API takes the first entry as OwnerId); members stay alphabetical.
        owners: [...team.ownerKeys]
          .map((key) => people.find((person) => person.key === key))
          .filter((person): person is DealPersonOption => person !== undefined),
        members: people.filter((person) => team.memberKeys.has(person.key)),
        dynamicFields,
      });
      const project = await ProjectsApi.addProject(payload);
      setCreated(project);
      onCreated?.(project);
    } catch (error: unknown) {
      setSubmitError(parseApiErrorMessage(error, "The Deal could not be created. Please try again."));
    } finally {
      setCreating(false);
    }
  };

  const resetAll = () => {
    setStep(1);
    setName("");
    setDescription("");
    setStart(toDateInputValue(new Date()));
    setEnd("");
    setPriority("Medium");
    setTemplateId(null);
    setDefaultsApplied(false);
    setTeam(emptyDealTeam());
    setAppliedTeamDefaultsKey(null);
    setTeamNotice("");
    setMemberSearch("");
    setErrors({});
    setSubmitError("");
    setCreated(null);
    setDynamicFields([]);
    setDynamicErrors({});
  };

  const handleDone = () => {
    if (onClose) onClose();
    else resetAll();
  };

  if (roster.loading) {
    return (
      <div className="create-deal__state" role="status">
        <FluentSpinner label="Loading Deal setup…" />
      </div>
    );
  }

  if (roster.error === CREATE_DEAL_ACCESS_DENIED) {
    return (
      <div className="create-deal__state" role="alert">
        <h3>Deal creation</h3>
        <p>
          Your TRIYO account does not have permission to view Deal members, which is required to create a Deal. Contact
          your TRIYO administrator for access.
        </p>
        {onClose ? (
          <button type="button" className="create-deal__button" onClick={onClose}>
            Close
          </button>
        ) : null}
      </div>
    );
  }

  if (roster.error) {
    return (
      <div className="create-deal__state" role="alert">
        <p>{roster.error}</p>
        <button type="button" className="create-deal__button" onClick={() => void roster.refetch()}>
          Try again
        </button>
      </div>
    );
  }

  if (created) {
    // Owners are a project concept in Triyo.Web. "Senior Deal Team" is an
    // Energy-specific dynamic field and is not interchangeable with ownership
    // for CME, Real Estate, or other Deal templates. Preserve selection order
    // here so the first displayed owner is the OwnerId posted to the API.
    const ownerNames = [...team.ownerKeys]
      .map((key) => people.find((person) => person.key === key)?.name)
      .filter((ownerName): ownerName is string => !!ownerName);
    const ownerSummary = ownerNames.map((ownerName, index) => (index === 0 ? `${ownerName} (primary)` : ownerName));
    const otherMemberNames = people
      .filter((person) => team.memberKeys.has(person.key) && !team.ownerKeys.has(person.key))
      .map((person) => person.name);
    const metaParts = [
      created.Id ? `Deal #${created.Id}` : null,
      `${formatLongCalendarDate(start)} – ${formatLongCalendarDate(end)}`,
      priority,
    ].filter(Boolean);
    return (
      <div className="create-deal__success" role="status">
        <CheckmarkCircleRegular className="create-deal__success-icon" />
        <h3>{created.Name || name.trim()} created</h3>
        <p className="create-deal__success-meta">{metaParts.join(" · ")}</p>
        <div className="create-deal__success-summary">
          <p>
            <strong>
              {ownerNames.length === 1 ? "Owner" : "Owners"} ({ownerNames.length}):
            </strong>{" "}
            {ownerSummary.join(", ")}
          </p>
          {otherMemberNames.length > 0 ? (
            <p>
              <strong>Deal members ({otherMemberNames.length}):</strong> {otherMemberNames.join(", ")}
            </p>
          ) : null}
        </div>
        <div className="create-deal__success-actions">
          <button type="button" className="create-deal__button create-deal__button--primary" onClick={handleDone}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="create-deal">
      <div className="create-deal__body">
        {step === 1 ? (
          <>
            <div className="create-deal__field">
              <label className="create-deal__field-label" htmlFor="create-deal-name">
                Deal Name <span className="create-deal__required">*</span>
              </label>
              <input
                id="create-deal-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  clearError("name");
                }}
                placeholder="e.g. Deal Maple"
              />
              {errors.name ? <p className="create-deal__field-error">{errors.name}</p> : null}
            </div>

            <div className="create-deal__field">
              <label className="create-deal__field-label" htmlFor="create-deal-description">
                Description
              </label>
              <textarea
                id="create-deal-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief mandate summary"
                rows={3}
              />
            </div>

            <div className="create-deal__grid">
              <div className="create-deal__field">
                <label className="create-deal__field-label" htmlFor="create-deal-start">
                  Start Date <span className="create-deal__required">*</span>
                </label>
                <DealDateInput
                  id="create-deal-start"
                  value={start}
                  max={end || undefined}
                  onChange={(e) => {
                    setStart(e.target.value);
                    clearError("start");
                  }}
                />
                {errors.start ? <p className="create-deal__field-error">{errors.start}</p> : null}
              </div>
              <div className="create-deal__field">
                <label className="create-deal__field-label" htmlFor="create-deal-end">
                  End Date <span className="create-deal__required">*</span>
                </label>
                <DealDateInput
                  id="create-deal-end"
                  value={end}
                  min={start || undefined}
                  onChange={(e) => {
                    setEnd(e.target.value);
                    clearError("end");
                  }}
                />
                {errors.end ? <p className="create-deal__field-error">{errors.end}</p> : null}
              </div>
            </div>

            <div className="create-deal__grid">
              {templates.length > 0 ? (
                <div className="create-deal__field">
                  <label className="create-deal__field-label" htmlFor="create-deal-template">
                    Template
                  </label>
                  <select
                    id="create-deal-template"
                    value={templateId ?? ""}
                    onChange={(e) => onTemplateChange(e.target.value)}
                  >
                    <option value="">None</option>
                    {templates.map((template) => (
                      <option key={template.Id} value={template.Id}>
                        {template.ProjectType}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="create-deal__field">
                <label className="create-deal__field-label" htmlFor="create-deal-priority">
                  Priority
                </label>
                <select
                  id="create-deal-priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as DealPriority)}
                >
                  {DEAL_PRIORITIES.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {dynamicLoading ? (
              <div className="create-deal__dynamic-loading" role="status">
                <FluentSpinner label="Loading template fields…" />
              </div>
            ) : null}

            {dynamicLoadError ? (
              <p className="create-deal__dynamic-load-error" role="alert">
                This template&rsquo;s additional fields could not be loaded. You can still create the Deal, then fill
                them in from the TRIYO web app.
              </p>
            ) : null}

            {!dynamicLoading ? (
              <DealDynamicFields
                fields={dynamicFields}
                errors={dynamicErrors}
                onChange={onDynamicFieldChange}
                onFilesChange={onDynamicFieldFilesChange}
                disabled={creating}
              />
            ) : null}
          </>
        ) : null}

        {step === 2 ? (
          <DealTeam
            options={people}
            selection={team}
            search={memberSearch}
            onSearchChange={setMemberSearch}
            onToggleMember={onToggleMember}
            onToggleOwner={onToggleOwner}
            onToggleAll={onToggleAllMembers}
            isMultiOwner={isMultiOwner}
            ownerSelectionLocked={!canTransferOwner}
            notice={teamNotice}
            error={errors.team}
          />
        ) : null}
      </div>

      <div className="create-deal__footer">
        {submitError ? (
          <p className="create-deal__submit-error" role="alert">
            {submitError}
          </p>
        ) : null}
        <div className="create-deal__footer-row">
          <span className="create-deal__progress">
            <span>
              Step {step} of {STEPS.length}
            </span>
            <StepBars step={step} />
          </span>
          <div className="create-deal__footer-actions">
            {step === 1 ? (
              onClose ? (
                <button type="button" className="create-deal__button" onClick={onClose}>
                  Cancel
                </button>
              ) : null
            ) : (
              <button
                type="button"
                className="create-deal__button"
                disabled={creating}
                onClick={() => {
                  setErrors({});
                  setSubmitError("");
                  setStep(step - 1);
                }}
              >
                Back
              </button>
            )}
            {step === 1 ? (
              <button
                type="button"
                className="create-deal__button create-deal__button--primary"
                onClick={nextFromStep1}
              >
                Next
              </button>
            ) : null}
            {step === 2 ? (
              <button
                type="button"
                className="create-deal__button create-deal__button--primary"
                disabled={creating}
                onClick={() => void handleCreate()}
              >
                {creating ? "Creating…" : "Create Deal"}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
